import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import URLS from "url/apiURL"
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import axios from "axios";

const { getSpecificPendingSpDocument } = URLS.ADMIN.REQUESTS.PENDINGREQUESTS
const { getSpecificApprovedSpDocument, getSpecificRejectedSpDocument } = URLS.ADMIN.USER.SERVICEPROVIDER

/** The API answers with the file itself, so an error arrives as a Blob rather than
 * JSON — read it back out so the admin sees the real reason (e.g. "No document has
 * been uploaded…") instead of a blank dialog. */
const errorMessageFrom = async (error) => {
    const body = error?.response?.data
    if (body instanceof Blob) {
        try { return JSON.parse(await body.text())?.message } catch { /* not JSON */ }
    }
    return body?.message || error?.message || 'Unable to load the document.'
}

const DocumentViewer = ({ rowData, type }) => {
    const [open, setOpen] = useState(false)
    const [fileUrl, setFileUrl] = useState('')
    const [fileType, setFileType] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    // Blob URLs leak until revoked; keep a handle so we can free the previous one.
    const objectUrlRef = useRef('')

    useEffect(() => {
        if (!open) return

        const fetchDocument = async () => {
            setLoading(true)
            setError('')
            const idField = (type === 'pending' || type === 'rejected') ? 'register_sp_id' : 'sp_id'
            const idValue = idField === 'sp_id' ? rowData?.sp_id : rowData?.register_sp_id
            const endpoint = type === 'pending' ? getSpecificPendingSpDocument
                : type === 'rejected' ? getSpecificRejectedSpDocument
                    : type === 'approved' ? getSpecificApprovedSpDocument : ''

            if (!endpoint || !idValue) {
                setError('Unable to load the document.')
                setLoading(false)
                return
            }

            try {
                const token = localStorage.getItem('access_tokenSP')
                const response = await axios.get(`${endpoint}?${idField}=${idValue}`, {
                    responseType: 'blob',
                    headers: { Authorization: `Bearer ${token}` },
                })
                // Trust the server's content type. The old code hardcoded
                // 'application/pdf', so an image (or an error body) was handed to the
                // PDF viewer and rendered as a blank frame.
                const contentType = response.headers?.['content-type'] || 'application/pdf'
                const url = URL.createObjectURL(new Blob([response.data], { type: contentType }))
                objectUrlRef.current = url
                setFileType(contentType)
                setFileUrl(url)
            } catch (e) {
                setError(await errorMessageFrom(e))
            } finally {
                setLoading(false)
            }
        }
        fetchDocument()
    }, [open, type, rowData])

    const handleClose = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = ''
        }
        setFileUrl('')
        setFileType('')
        setError('')
        setOpen(false)
    }

    const renderBody = () => {
        if (loading) return (
            <Box className='flex ai-center jc-center column' height='100%' gap={2}>
                <CircularProgress color='options' />
                <Typography color='text.secondary'>Loading document…</Typography>
            </Box>
        )
        // Covers both "this SP never uploaded one" and "the file is missing from the
        // server" — the backend distinguishes them and we show its message as-is.
        if (error) return (
            <Box className='flex ai-center jc-center column' height='100%' gap={2}>
                <DescriptionOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography fontSize='1.1rem' color='text.secondary' textAlign='center'>{error}</Typography>
            </Box>
        )
        if (!fileUrl) return null
        if (fileType.startsWith('image/')) return (
            <Box className='flex ai-center jc-center' height='100%'>
                <img src={fileUrl} alt='Service provider document' style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
        )
        return (
            <iframe
                src={fileUrl}
                title='Service provider document'
                style={{ width: '100%', height: '100%', border: 'none' }}
            />
        )
    }

    return (
        <>
            {!open ?
                <IconButton color='options' onClick={() => setOpen(true)}>
                    <Box className='flex ai-flex-start jc-center column'>
                        <Typography fontSize={9}> Document</Typography>
                        <ArticleIcon style={{ cursor: 'pointer', marginRight: '5px' }} />
                    </Box>
                </IconButton>
                :
                <Dialog fullScreen fullWidth open={open} onClose={handleClose}>
                    <DialogContent sx={{ p: 2 }}>{renderBody()}</DialogContent>
                    <DialogActions>
                        {fileUrl && !error && (
                            <Button color='options' onClick={() => window.open(fileUrl, '_blank')}>
                                Open in new tab
                            </Button>
                        )}
                        <Button color='options' variant='contained' onClick={handleClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            }
        </>
    )
}

export default DocumentViewer
